import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { AgGridReact } from 'ag-grid-react';
import { useGridTheme } from '@/config/useGridTheme';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import {
  MongoMergeWhenMatched,
  MongoMergeWhenNotMatched,
  MongoAggregationOperator,
} from '@/services/graphql';

export interface AggregationFormData {
  targetCollection: string;
  groupFields: string[];
  mergeOnFields: string[];
  aggregationFields: AggregationFieldRow[];
  whenMatched: MongoMergeWhenMatched;
  whenNotMatched: MongoMergeWhenNotMatched;
  useAccumulativeMerge: boolean;
}

interface AggregationFieldRow {
  id: string;
  sourceFieldName: string;
  outputFieldName: string;
  operator: MongoAggregationOperator;
}

interface SftpMongoAggregationDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: AggregationFormData) => void;
  aggregation?: {
    targetCollection: string;
    groupFields: string[];
    mergeOnFields: string[];
    aggregationFields: { sourceFieldName: string; outputFieldName: string; operator: MongoAggregationOperator }[];
    whenMatched: MongoMergeWhenMatched;
    whenNotMatched: MongoMergeWhenNotMatched;
    useAccumulativeMerge: boolean;
  } | null;
  destinationName: string;
}

const OPERATORS = Object.values(MongoAggregationOperator);

const DeleteButtonRenderer: React.FC<ICellRendererParams> = (props) => {
  const handleDelete = () => {
    if (props.context?.deleteRow) {
      props.context.deleteRow(props.node.data.id);
    }
  };

  return (
    <Button
      size="small"
      color="error"
      variant="contained"
      startIcon={<DeleteIcon />}
      onClick={handleDelete}
      sx={{ minWidth: 'auto', fontSize: '0.8125rem', py: 0.5, px: 1.5 }}
    >
      Delete
    </Button>
  );
};

export const SftpMongoAggregationDialog: React.FC<SftpMongoAggregationDialogProps> = ({
  open,
  onClose,
  onSave,
  aggregation,
  destinationName,
}) => {
  const gridRef = useRef<AgGridReact<AggregationFieldRow>>(null);
  const gridTheme = useGridTheme();
  const [formData, setFormData] = useState<AggregationFormData>({
    targetCollection: '',
    groupFields: [],
    mergeOnFields: [],
    aggregationFields: [],
    whenMatched: MongoMergeWhenMatched.Replace,
    whenNotMatched: MongoMergeWhenNotMatched.Insert,
    useAccumulativeMerge: false,
  });
  const [chipInput, setChipInput] = useState('');
  const [mergeOnChipInput, setMergeOnChipInput] = useState('');
  const [nextId, setNextId] = useState(1);

  useEffect(() => {
    if (open && aggregation) {
      const fields = aggregation.aggregationFields.map((f, i) => ({
        ...f,
        id: `existing-${i}`,
      }));
      setFormData({
        targetCollection: aggregation.targetCollection,
        groupFields: [...aggregation.groupFields],
        mergeOnFields: [...(aggregation.mergeOnFields || [])],
        aggregationFields: fields,
        whenMatched: aggregation.whenMatched,
        whenNotMatched: aggregation.whenNotMatched,
        useAccumulativeMerge: aggregation.useAccumulativeMerge ?? false,
      });
      setNextId(fields.length + 1);
    } else if (open && !aggregation) {
      setFormData({
        targetCollection: '',
        groupFields: [],
        mergeOnFields: [],
        aggregationFields: [],
        whenMatched: MongoMergeWhenMatched.Replace,
        whenNotMatched: MongoMergeWhenNotMatched.Insert,
        useAccumulativeMerge: false,
      });
      setNextId(1);
    }
    setChipInput('');
    setMergeOnChipInput('');
  }, [open, aggregation]);

  const handleAddGroupField = () => {
    const value = chipInput.trim();
    if (value && !formData.groupFields.includes(value)) {
      setFormData(prev => ({ ...prev, groupFields: [...prev.groupFields, value] }));
      setChipInput('');
    }
  };

  const handleRemoveGroupField = (field: string) => {
    setFormData(prev => ({
      ...prev,
      groupFields: prev.groupFields.filter(f => f !== field),
    }));
  };

  const handleAddMergeOnField = () => {
    const value = mergeOnChipInput.trim();
    if (value && !formData.mergeOnFields.includes(value)) {
      setFormData(prev => ({ ...prev, mergeOnFields: [...prev.mergeOnFields, value] }));
      setMergeOnChipInput('');
    }
  };

  const handleRemoveMergeOnField = (field: string) => {
    setFormData(prev => ({
      ...prev,
      mergeOnFields: prev.mergeOnFields.filter(f => f !== field),
    }));
  };

  const handleAddAggField = () => {
    const newRow: AggregationFieldRow = {
      id: `new-${nextId}`,
      sourceFieldName: '',
      outputFieldName: '',
      operator: MongoAggregationOperator.Sum,
    };
    setFormData(prev => ({ ...prev, aggregationFields: [...prev.aggregationFields, newRow] }));
    setNextId(prev => prev + 1);
  };

  const columnDefs = useMemo<ColDef<AggregationFieldRow>[]>(() => [
    { field: 'sourceFieldName', headerName: 'Source Field', editable: true, flex: 1, cellDataType: 'text' },
    { field: 'outputFieldName', headerName: 'Output Field', editable: true, flex: 1, cellDataType: 'text' },
    {
      field: 'operator',
      headerName: 'Operator',
      editable: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: { values: OPERATORS },
      flex: 1,
    },
    {
      headerName: '',
      width: 100,
      cellRenderer: DeleteButtonRenderer,
      editable: false,
      sortable: false,
      filter: false,
    },
  ], []);

  const gridContext = useMemo(() => ({
    deleteRow: (id: string) => {
      setFormData(prev => ({
        ...prev,
        aggregationFields: prev.aggregationFields.filter(r => r.id !== id),
      }));
    },
  }), []);

  const handleSave = () => {
    if (gridRef.current?.api) {
      gridRef.current.api.stopEditing();
    }
    onSave(formData);
    onClose();
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Aggregation Config - {destinationName}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Target Collection"
              value={formData.targetCollection}
              onChange={(e) => setFormData(prev => ({ ...prev, targetCollection: e.target.value }))}
            />
          </Grid>

          {/* Group Fields */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>Group Fields</Typography>
            <Box display="flex" gap={1} flexWrap="wrap" mb={1}>
              {formData.groupFields.map(f => (
                <Chip key={f} label={f} onDelete={() => handleRemoveGroupField(f)} />
              ))}
            </Box>
            <Box display="flex" gap={1}>
              <TextField
                size="small"
                label="Add field"
                value={chipInput}
                onChange={(e) => setChipInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddGroupField(); } }}
              />
              <Button variant="outlined" size="small" onClick={handleAddGroupField}>Add</Button>
            </Box>
          </Grid>

          {/* Aggregation Fields Grid */}
          <Grid item xs={12}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle2">Aggregation Fields</Typography>
              <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleAddAggField}>
                Add Field
              </Button>
            </Box>
            {formData.aggregationFields.length === 0 ? (
              <Alert severity="info">No aggregation fields defined.</Alert>
            ) : (
              <Box sx={{ height: 250, width: '100%' }}>
                <AgGridReact<AggregationFieldRow>
                  ref={gridRef}
                  rowData={formData.aggregationFields}
                  columnDefs={columnDefs}
                  context={gridContext}
                  theme={gridTheme}
                  onCellValueChanged={(event) => {
                    setFormData(prev => ({
                      ...prev,
                      aggregationFields: prev.aggregationFields.map(r =>
                        r.id === event.data.id ? event.data : r
                      ),
                    }));
                  }}
                  defaultColDef={{ resizable: true, sortable: true, editable: true }}
                  getRowId={(params) => params.data.id}
                  singleClickEdit={true}
                />
              </Box>
            )}
          </Grid>

          {/* Merge On Fields */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>Merge On Fields</Typography>
            <Box display="flex" gap={1} flexWrap="wrap" mb={1}>
              {formData.mergeOnFields.map(f => (
                <Chip key={f} label={f} onDelete={() => handleRemoveMergeOnField(f)} />
              ))}
            </Box>
            <Box display="flex" gap={1}>
              <TextField
                size="small"
                label="Add field"
                value={mergeOnChipInput}
                onChange={(e) => setMergeOnChipInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddMergeOnField(); } }}
              />
              <Button variant="outlined" size="small" onClick={handleAddMergeOnField}>Add</Button>
            </Box>
          </Grid>

          {/* Merge Behavior */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth disabled={formData.useAccumulativeMerge}>
              <InputLabel>When Matched</InputLabel>
              <Select
                value={formData.whenMatched}
                label="When Matched"
                onChange={(e) => setFormData(prev => ({ ...prev, whenMatched: e.target.value as MongoMergeWhenMatched }))}
              >
                {Object.values(MongoMergeWhenMatched).map(v => (
                  <MenuItem key={v} value={v}>{v}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>When Not Matched</InputLabel>
              <Select
                value={formData.whenNotMatched}
                label="When Not Matched"
                onChange={(e) => setFormData(prev => ({ ...prev, whenNotMatched: e.target.value as MongoMergeWhenNotMatched }))}
              >
                {Object.values(MongoMergeWhenNotMatched).map(v => (
                  <MenuItem key={v} value={v}>{v}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.useAccumulativeMerge}
                  onChange={(e) => setFormData(prev => ({ ...prev, useAccumulativeMerge: e.target.checked }))}
                />
              }
              label="Use Accumulative Merge (pipeline-based whenMatched with $add for Sum fields)"
            />
            {formData.useAccumulativeMerge && (
              <Alert severity="info" sx={{ mt: 1 }}>
                When enabled, the whenMatched dropdown is ignored. Sum fields will accumulate values
                across imports using $add, and non-Sum fields will use the latest value.
              </Alert>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} startIcon={<CancelIcon />}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" startIcon={<SaveIcon />}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};
